<template>
  <property-report-card
    title="Report Map"
    description="View your subject and comparables on a map."
    :printing-mode="printingMode"
    :hide-header="!printingMode"
  >
    <q-skeleton v-show="loading && !printingMode" class="google-map" animation="blink" />
    <div
      class="row items-start justify-start q-gutter-x-xl"
      :class="{
        'q-gutter-md': printingMode
      }"
    >
      <div class="col-4 q-mr-md">
        <q-list v-if="printingMode && !legacyPrintingMode" dense bordered separator>
          <q-item dense v-for="property in allProperties" :key="property.id">
            <q-item-section avatar>
              <div
                class="absolute q-ma-xs text-white text-bold"
                style="border-radius: 50%; width: 24px; height: 24px; z-index: 1"
                :style="{
                  'background-color': subjectDetail?.id === property.id ? '#6365f1' : '#E97F08',
                  left: deleteEnabled ? '25px' : ''
                }"
              >
                <div class="flex justify-center items-center" style="height: 100%">
                  <i
                    v-if="subjectDetail?.id === property.id"
                    class="material-icons"
                    style="font-size: 14px"
                    >star</i
                  >
                  <span v-else>
                    {{
                      (
                        filteredProperties.findIndex(({ prop }) => prop.id === property.id) +
                        (subjectVisible ? 0 : 1)
                      ).toString()
                    }}
                  </span>
                </div>
              </div>
            </q-item-section>
            <q-item-section>
              <div style="font-size: 16px">
                {{ property.building_name }}
              </div>
              <div class="ellipsis" style="max-width: 300px; font-size: 0.8rem">
                {{ property.street_address }}, {{ property.city }}, {{ property.state }},
                {{ property.zip_code }}
              </div>
              <div class="row q-gutter-lg q-py-xs">
                <div>
                  <div class="metric-label q-mr-xs">Year Built</div>
                  {{ property.year_built || '-' }}
                </div>
                <div>
                  <div class="metric-label">Number of Units</div>
                  {{ property.number_units || '-' }}
                </div>
                <div v-if="similarities[property.id]?.distance_miles">
                  <div class="metric-label">Distance</div>
                  <q-icon name="place" />
                  {{ safeMiles(similarities[property.id]?.distance_miles) }} mi
                </div>
                <div v-if="similarities[property.id]?.overall">
                  <div class="metric-label">Similarity</div>
                  <q-icon name="data_usage" />
                  {{ formatPercent(similarities[property.id].overall, 0) }}
                </div>
              </div>
            </q-item-section>
          </q-item>
        </q-list>
      </div>
      <google-map
        v-show="!loading || printingMode"
        ref="mapRef"
        data-private
        :api-promise="apiPromise"
        class="google-map"
        :api-key="apiKey"
        :max-zoom="16"
        :styles="theme"
      >
        <!-- Next candidates as small circle markers -->
        <google-marker
          v-for="candidate in nextCandidatesWithCoords"
          :key="candidate.id"
          :options="{
            position: { lat: candidate.lat as number, lng: candidate.lon as number },
            optimized: true,
            icon: candidateCircleIcon,
            zIndex: 1
          }"
          @click="openMarker"
        >
          <info-window
            ref="CandidateInfoWindow"
            :options="{ headerDisabled: true }"
            class="text-black"
          >
            <map-property-card
              :property="candidate"
              :show-image="false"
              :delete-enabled="false"
              :is-candidate="true"
              :disable-add="existingPropertyIds.has(candidate.id)"
              @close="closeInfoWindow"
              @add="onAddCandidate(candidate)"
            />
          </info-window>
        </google-marker>

        <google-marker
          v-for="(prop, idx) in allProperties"
          :key="prop.id"
          :options="{
            position: { lat: prop.lat, lng: prop.lon },
            optimized: true,
            icon: {
              url: subjectDetail?.id === prop.id ? '/marker-primary.svg' : '/marker-accent.svg',
              labelOrigin: point || undefined
            },
            label: {
              text:
                subjectDetail?.id === prop.id
                  ? subjectStarIcon
                  : (idx + (subjectVisible ? 0 : 1)).toString(),
              color: subjectDetail?.id === prop.id ? '#6365f1' : '#FF8800',
              fontWeight: 'bold'
            },
            zIndex: allProperties.length - idx
          }"
          @click="openMarker"
        >
          <info-window ref="InfoWindowTest" :options="{ headerDisabled: true }" class="text-black">
            <map-property-card
              :property="prop"
              :show-image="!errorLoadingImage[prop.id]"
              :similarities="similarities[prop.id]"
              :delete-enabled="deleteEnabled"
              :is-candidate="false"
              :can-create-property-reports="canCreatePropertyReports"
              @close="closeInfoWindow"
              @delete="deleteAddress(prop.id)"
              @image-error="errorLoadingImage[prop.id] = true"
            />
          </info-window>
        </google-marker>
      </google-map>
    </div>
  </property-report-card>
</template>

<script lang="ts">
import { type PropType, watch } from 'vue'
import {
  type Availability,
  type AvailabilityPeriod,
  type Price,
  type PropertyComparableResponse,
  type PropertyDetailsResponse,
  type Similarities
} from 'common'
import { GoogleMap, Marker as GoogleMarker, InfoWindow } from 'vue3-google-map'
import { formatPercent, formatQuality, lightMapTheme, darkMapTheme } from '@/utils'
import apiPromise from '@/setups/googleMaps'

import PropertyReportCard from '@/components/PropertyReportCard.vue'
import MapPropertyCard from '@/components/PropertyReports/MapPropertyCard.vue'
import { useUserStore } from '@/stores/user.ts'

export default {
  name: 'PropertyCompareGoogleMap',
  components: {
    PropertyReportCard,
    GoogleMap,
    GoogleMarker,
    InfoWindow,
    MapPropertyCard
  },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    nextCandidates: {
      type: Array as PropType<PropertyComparableResponse[]>,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    deleteEnabled: {
      type: Boolean,
      default: false
    },
    similarities: {
      type: Object as PropType<{ [key: string]: Similarities }>,
      default: () => ({})
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    legacyPrintingMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['delete-address', 'add-candidate', 'map-snapshot', 'map-snapshot-alternative'],
  data() {
    return {
      apiPromise: apiPromise as unknown as Promise<typeof google>,
      openedMarkerID: null as null | string,
      apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
      mapRef: null as null | typeof GoogleMap,
      errorLoadingImage: {} as Record<string, boolean>,
      point: null as google.maps.Point | null
    }
  },
  computed: {
    subjectStarIcon() {
      return this.printingMode ? ' ' : '★'
    },
    subjectVisible() {
      return this.filteredProperties.some((_) => _.prop.id === this.subjectDetail?.id)
    },
    theme() {
      return this.$q.dark.isActive ? darkMapTheme : lightMapTheme
    },
    allProperties() {
      return this.filteredProperties.map((_) => _.prop)
    },
    existingPropertyIds(): Set<string> {
      return new Set(this.allProperties.map((p) => p.id))
    },
    canCreatePropertyReports() {
      return useUserStore().canCreatePropertyReports
    },
    nextCandidatesWithCoords(): PropertyComparableResponse[] {
      return (this.nextCandidates || []).filter(
        (c) => typeof c.lat === 'number' && typeof c.lon === 'number'
      ) as PropertyComparableResponse[]
    },
    candidateCircleIcon() {
      // Small orange circle, matches accent color used for comparables
      const svg =
        '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10">' +
        '<circle cx="5" cy="5" r="4" fill="#FF8800" /></svg>'
      return { url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}` }
    }
  },
  watch: {
    allProperties() {
      this.onLoad()
    }
  },
  mounted() {
    this.mapRef = this.$refs['mapRef'] as typeof GoogleMap
    watch(
      () => this.mapRef?.ready,
      () => {
        this.onLoad()
      }
    )

    this.apiPromise.then(() => {
      this.point = new google.maps.Point(16, 17)
    })
  },
  methods: {
    formatPercent,
    formatQuality,
    safeMiles(value?: number | null) {
      if (typeof value === 'number') {
        return value.toFixed(2)
      }
      return '-'
    },
    onAddCandidate(candidate: PropertyComparableResponse) {
      this.$emit('add-candidate', candidate)
      this.closeInfoWindow()
    },
    closeInfoWindow() {
      const closeRefs = (refOrArray: any) => {
        if (!refOrArray) {
          return
        }
        const refs = Array.isArray(refOrArray) ? refOrArray : [refOrArray]
        for (const ref of refs) {
          if (ref && typeof ref.close === 'function') {
            ref.close()
          }
        }
      }
      // Close both property and candidate info windows
      closeRefs(this.$refs['InfoWindowTest'])
      closeRefs(this.$refs['CandidateInfoWindow'])
    },
    openMarker() {
      this.closeInfoWindow()
    },
    deleteAddress(rowId: string) {
      this.$emit('delete-address', rowId)
    },
    createStaticMapUrl() {
      const mapRefVal = this.$refs.mapRef as typeof GoogleMap
      const gmap = mapRefVal?.map
      if (!gmap) {
        return
      }

      const width = 450
      const height = 300

      //zoom is set so the Static Maps API auto-fits to include all markers
      const params = new URLSearchParams({
        size: `${width}x${height}`,
        scale: '2',
        maptype: 'roadmap',
        key: this.apiKey
      })

      // Add subject marker
      if (this.subjectDetail?.lat && this.subjectDetail?.lon) {
        params.append(
          'markers',
          `color:0x6365f1|${this.subjectDetail.lat},${this.subjectDetail.lon}`
        )
      }

      // Add comparable markers
      const comparables = this.allProperties.filter(
        (property) => property.id !== this.subjectDetail?.id
      )
      if (comparables.length > 0) {
        const compMarkers = comparables
          .filter(
            (property) => typeof property.lat === 'number' && typeof property.lon === 'number'
          )
          .map((property) => `${property.lat},${property.lon}`)
          .join('|')
        if (compMarkers) {
          params.append('markers', `color:0xFF8800|${compMarkers}`)
        }
      }

      const url = `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`
      this.$emit('map-snapshot', url)
    },
    async createStaticMapUrlAlternative() {
      const mapRefVal = this.$refs.mapRef as typeof GoogleMap
      const gmap = mapRefVal?.map
      if (!gmap) {
        return
      }

      // Wait for map tiles and marker overlays to render
      await new Promise((resolve) => setTimeout(resolve, 500))
      const mapDiv = gmap.getDiv()
      const { default: html2canvas } = await import('html2canvas')
      const canvas = await html2canvas(mapDiv, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        scale: 2
      })

      const dataUrl = canvas.toDataURL('image/png')
      this.$emit('map-snapshot-alternative', dataUrl)
    },
    onLoad() {
      if (this.mapRef === null) {
        console.warn('mapRef is null')
      } else if (this.mapRef.ready) {
        const bounds = new google.maps.LatLngBounds()
        this.allProperties.forEach((d) => {
          if (typeof d.lat === 'number' && typeof d.lon === 'number') {
            bounds.extend(new google.maps.LatLng(d.lat, d.lon))
          }
        })
        const mapRefVal = this.$refs.mapRef as typeof GoogleMap
        const gmap = mapRefVal.map
        gmap.fitBounds(bounds)
      }
    }
  }
}
</script>

<style scoped lang="scss">
@media print {
  .google-map {
    width: 50% !important;
  }
}
.google-map {
  width: 100%;
  min-height: 300px;
  height: calc(100vh / 3);
}
.metric-label {
  font-size: 0.8em;
  font-weight: 400;
  color: #6c757d;
}
</style>
